import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import Footer from "@/components/Footer";

import NewsCard from "@/components/NewsCard";
import NewsFilters from "@/components/NewsFilters";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";
import { newsArticles } from "@/data/newsData";
import { trackEvent } from "@/lib/analytics";

const ALL = "All";

const News = () => {
  const sortedArticles = useMemo(
    () => [...newsArticles].sort((a, b) => b.date.getTime() - a.date.getTime()),
    []
  );

  const categories = useMemo(
    () => Array.from(new Set(newsArticles.map((a) => a.category))).sort(),
    []
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || ALL;
  const initialQuery = searchParams.get("q") || "";

  const [selectedCategory, setSelectedCategory] = useState<string>(
    categories.includes(initialCategory) || initialCategory === ALL ? initialCategory : ALL
  );
  const [query, setQuery] = useState<string>(initialQuery);

  // Sync state -> URL
  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedCategory && selectedCategory !== ALL) next.set("category", selectedCategory);
    if (query.trim()) next.set("q", query.trim());
    setSearchParams(next, { replace: true });
  }, [selectedCategory, query, setSearchParams]);

  // Debounced analytics
  const analyticsTimer = useRef<number | null>(null);
  useEffect(() => {
    if (analyticsTimer.current) window.clearTimeout(analyticsTimer.current);
    analyticsTimer.current = window.setTimeout(() => {
      trackEvent("news_filter_change", {
        category: selectedCategory,
        query_length: query.trim().length,
      });
    }, 300);
    return () => {
      if (analyticsTimer.current) window.clearTimeout(analyticsTimer.current);
    };
  }, [selectedCategory, query]);

  const filteredArticles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortedArticles.filter((article) => {
      const matchesCategory = selectedCategory === ALL || article.category === selectedCategory;
      if (!matchesCategory) return false;
      if (!q) return true;
      return (
        article.title.toLowerCase().includes(q) ||
        article.excerpt.toLowerCase().includes(q)
      );
    });
  }, [sortedArticles, selectedCategory, query]);

  const handleClear = () => {
    setSelectedCategory(ALL);
    setQuery("");
  };

  const newsJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "News & Insights",
    description:
      "Latest news and insights from AI-labbet on AI trends, regulations, best practices, and industry developments.",
    url: "https://www.ailabbet.com/news",
    isPartOf: {
      "@type": "WebSite",
      name: "AI-labbet",
      url: "https://www.ailabbet.com/",
    },
    hasPart: sortedArticles.slice(0, 20).map((article) => ({
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date.toISOString(),
      articleSection: article.category,
    })),
  };

  return (
    <>
      <SeoHead
        title="News & Insights | AI-labbet"
        description="Latest news and insights from AI-labbet on AI trends, regulations, best practices, and industry developments."
        path="/news"
        jsonLd={newsJsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        <PageBreadcrumb currentPage="News" />
        <main>
          <Section
            eyebrow="All articles"
            title={<>Latest News &amp; <span className="text-gradient-brand">Insights</span></>}
            titleAs="h1"
            description="Stay updated on AI innovations, industry trends, and company updates."
            containerSize="wide"
            grid
          >
            <NewsFilters
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              query={query}
              onQueryChange={setQuery}
              resultCount={filteredArticles.length}
              totalCount={sortedArticles.length}
              onClear={handleClear}
            />

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 px-6 rounded-lg border border-border bg-card/50">
                <p className="text-lg font-medium mb-2">No articles match your filters</p>
                <p className="text-muted-foreground mb-6">
                  Try a different category or search term.
                </p>
                <Button variant="primary" size="sm" onClick={handleClear}>
                  Clear filters
                </Button>
              </div>
            )}
          </Section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default News;
